"use strict";
/**
 * Hockey pad rendering (docs/nhl-model/PLAN.md §4.5). Pure: markup in,
 * markup out. Upright, the phone is the information view: the banner (score,
 * clock, price, the paper ladder over Kalshi's full depth), the pre-game
 * panel, the price chart and the event log with each tap's market reaction;
 * the only controls are KILL, the typed clock and UNDO. Sideways, it is the
 * pad (renderHockeyLandscape). Buttons fire on pointerdown
 * (handlers/hockey-pad-handler.ts), so the button grid is rendered ONCE and
 * only the live parts are re-rendered.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.clockRun = exports.stoppedFor = exports.holdAllowed = exports.suddenDeathAhead = exports.holdLeft = exports.holdActive = exports.HOLD_DEFAULT_SECONDS = exports.HOCKEY_LABELS = void 0;
exports.formatHockeyClock = formatHockeyClock;
exports.localSecondsRemaining = localSecondsRemaining;
exports.renderHockeyPad = renderHockeyPad;
exports.renderHockeyPortrait = renderHockeyPortrait;
exports.renderHockeyLandscape = renderHockeyLandscape;
exports.renderMoreSheet = renderMoreSheet;
exports.landscapePaint = landscapePaint;
exports.renderLadder = renderLadder;
exports.renderDepth = renderDepth;
exports.renderHockeyBanner = renderHockeyBanner;
exports.draftFromOdds = draftFromOdds;
exports.oddsFromDraft = oddsFromDraft;
exports.shinHome = shinHome;
exports.renderAnchors = renderAnchors;
exports.renderPregamePanel = renderPregamePanel;
exports.eventLabel = eventLabel;
exports.renderReaction = renderReaction;
exports.renderHockeyHistory = renderHockeyHistory;
exports.renderSeriesSummary = renderSeriesSummary;
exports.renderHockeyChart = renderHockeyChart;
exports.parseClockInput = parseClockInput;
const win_prob_1 = require("../types/win-prob");
const win_prob_display_1 = require("./win-prob-display");
exports.HOCKEY_LABELS = {
    game_type: "game type",
    period_start: "PERIOD START",
    period_end: "PERIOD END",
    final: "FINAL",
    clock_start: "▶ START",
    clock_stop: "■ STOP",
    clock_set: "SET",
    clock_nudge: "nudge",
    goal: "GOAL",
    goal_pending: "REVIEW",
    goal_confirmed: "GOOD GOAL",
    goal_overturned: "NO GOAL",
    penalty_minor: "MIN",
    penalty_double_minor: "2×MIN",
    penalty_major: "MAJ",
    penalty_misconduct: "misconduct",
    penalty_coincidental: "COINCIDENTAL",
    penalty_expired: "PEN OVER",
    penalty_shot: "PEN SHOT",
    penalty_shot_miss: "PS MISS",
    delayed_penalty: "DELAYED",
    delayed_penalty_off: "DELAYED OFF",
    goalie_pulled: "GOALIE OUT",
    goalie_returned: "GOALIE IN",
    strength_set: "strength",
    danger: "DANGER",
    scoring_chance: "CHANCE",
    shootout_winner: "SO WIN",
    hold: "HOLD",
    hold_off: "HOLD OFF",
    undo: "UNDO",
};
/** What HOLD sends; the server caps it (`params.ts` → `quote.holdDefaultSeconds` / `holdMaxSeconds`, PLAN.md §5.3). */
exports.HOLD_DEFAULT_SECONDS = 1200;
/** HOLD ↔ HOLD OFF, swapped in place by the state like the goalie cell; the handler disables both while the clock runs. */
function holdCell(cls) {
    return `<div class="hp-swap hp-hold-cell" data-swap="hold" data-mode="idle">${button("hold", `${cls} hp-swaps hp-when-idle hp-hold`, "HOLD", null, exports.HOLD_DEFAULT_SECONDS)}${button("hold_off", `${cls} hp-swaps hp-when-held hp-hold-off`)}</div>`;
}
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
function button(kind, cls, label = exports.HOCKEY_LABELS[kind], side = null, value = null) {
    const sideAttr = side ? ` data-side="${side}"` : "";
    const valueAttr = value === null ? "" : ` data-value="${value}"`;
    return `<button type="button" class="tap-btn ${cls}" data-ev-kind="${kind}"${sideAttr}${valueAttr}>${escapeHtml(label)}</button>`;
}
/** "P2 12:34", "OT 3:10", "SO", "intermission", "pre-game", "final". */
function formatHockeyClock(state, secondsRemaining) {
    if (state.status === "pre")
        return "pre-game";
    if (state.status === "final")
        return state.shootout ? "final (SO)" : state.period > 3 ? "final (OT)" : "final";
    if (state.shootout)
        return "SHOOTOUT";
    const s = Math.max(0, Math.ceil(secondsRemaining));
    const mmss = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    const label = state.period <= 3 ? `P${state.period}` : state.gameType === "playoff" ? `OT${state.period - 3}` : "OT";
    return state.status === "intermission" ? `${label} end · intermission` : `${label} ${mmss}`;
}
/** The clock as the pad reads it between polls: the anchor advanced on the server's clock. */
function localSecondsRemaining(state, nowServerMs) {
    const c = state.clock;
    if (!c.running || c.anchorMs === null)
        return c.secondsRemaining;
    return Math.max(0, c.secondsRemaining - (nowServerMs - c.anchorMs) / 1000);
}
const mmss = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
const holdActive = (state, nowServerMs) => state.holdUntilMs !== null && state.holdUntilMs > nowServerMs;
exports.holdActive = holdActive;
const holdLeft = (state, nowServerMs) => mmss(Math.max(0, Math.ceil(((state.holdUntilMs ?? nowServerMs) - nowServerMs) / 1000)));
exports.holdLeft = holdLeft;
/** Sudden death next or under way (the server's `suddenDeath`): overtime, a shootout, or a level score at the third-period horn. */
const suddenDeathAhead = (state) => state.status !== "final" && (state.period > 3 || state.shootout || (state.status === "intermission" && state.period >= 3 && state.homeScore === state.awayScore));
exports.suddenDeathAhead = suddenDeathAhead;
/** A hold can be declared only with play dead and no sudden death next (the reducer refuses it otherwise). */
const holdAllowed = (state) => (state.status === "in" || state.status === "intermission") && !state.clock.running && !(0, exports.suddenDeathAhead)(state);
exports.holdAllowed = holdAllowed;
/** Seconds since the clock stopped in play, or null when it runs or play is not live. */
const stoppedFor = (state, nowServerMs) => state.status === "in" && !state.clock.running && state.clockStoppedMs !== null ? Math.max(0, Math.floor((nowServerMs - state.clockStoppedMs) / 1000)) : null;
exports.stoppedFor = stoppedFor;
/** The centre column's clock state: running, stopped in play, or dead (intermission, pre-game, final). */
const clockRun = (state) => (state.clock.running ? "running" : state.status === "in" ? "stopped" : "dead");
exports.clockRun = clockRun;
function renderHockeyPad(teams, title) {
    return `<div class="tap-pad hp hp-pad-v2">${renderHockeyPortrait(teams, title)}${renderHockeyLandscape(teams, title)}</div>`;
}
/**
 * The upright view: banner, status, pre-game panel, the typed clock and UNDO,
 * then the price chart and the event log. No tap grid: that is the landscape.
 */
function renderHockeyPortrait(teams, title) {
    return `<div class="hp-portrait">
		<div class="tap-pad-head">
			<button type="button" class="hp-exit btn btn-small">Games</button>
			<span class="tap-pad-title">${escapeHtml(title)}</span>
			<button type="button" class="tap-btn tap-pull hp-kill">KILL</button>
		</div>
		<div class="hp-portrait-hint">Turn the phone sideways for the pad · ${escapeHtml(teams.away)} @ ${escapeHtml(teams.home)}</div>
		<div class="hp-banner" role="status"></div>
		<div class="tap-pad-status" role="status"></div>
		<div class="hp-pregame"></div>
		<div class="hp-controls">
			<span class="hp-clock-set"><input class="hp-clock-input" inputmode="numeric" placeholder="mm:ss" aria-label="Clock, minutes and seconds remaining" maxlength="5" /><button type="button" class="tap-btn hp-set">${escapeHtml(exports.HOCKEY_LABELS.clock_set)}</button></span>
			${holdCell("hp-ctl-btn")}
			${button("undo", "tap-undo hp-undo")}
		</div>
		<div class="hp-series-summary"></div>
		<div class="hp-chart"></div>
		<ol class="tap-history hp-eventlog" aria-label="Events and the market's reaction"></ol>
	</div>`;
}
/**
 * The two-thumb landscape layout, the NBA recorder's grammar (ai/nba/N8-taps/PLAN.md §11.1):
 * a half per team in its colour, mirrored so GOAL sits under each outer thumb, a shared centre
 * strip, no scrolling. Each half's band carries the team's win probability; the line under it
 * its Kalshi touch and the paper ladder's first level. Two cells swap in place behind a 250 ms
 * gate (handlers/hockey-pad-handler.ts): REVIEW ↔ GOOD GOAL / NO GOAL, GOALIE OUT ↔ GOALIE IN.
 * START and STOP are both always present, the current state filled (a double tap repeats an
 * idempotent action instead of reversing it: PLAN.md §6.5). The rare kinds live in the MORE sheet.
 */
function renderHockeyLandscape(teams, title) {
    const half = (side) => {
        const t = side === "home" ? teams.home : teams.away;
        const goal = button("goal", "hp-l-btn hp-l-goal", `GOAL ${t}`, side);
        const chance = button("scoring_chance", "hp-l-btn hp-chance-btn", "CHANCE", side);
        // One boxed pair: REVIEW over the goalie cell; while that side's goal is under review the
        // pair becomes GOOD GOAL over NO GOAL, full width (a review stops play, so the goalie
        // cell can wait). The goalie cell swaps OUT ↔ IN on its own.
        const swaps = `<div class="hp-swap hp-l-pair" data-swap="review" data-side="${side}" data-mode="idle">
				${button("goal_pending", "hp-l-btn hp-swaps hp-when-idle hp-l-review", "REVIEW", side)}
				<div class="hp-swap hp-l-swapcell hp-when-idle" data-swap="goalie" data-side="${side}" data-mode="in">
					${button("goalie_pulled", "hp-l-btn hp-swaps hp-when-in", "GOALIE OUT", side)}
					${button("goalie_returned", "hp-l-btn hp-swaps hp-when-out", "GOALIE IN", side)}
				</div>
				${button("goal_confirmed", "hp-l-btn hp-swaps hp-when-pending hp-l-confirm")}
				${button("goal_overturned", "hp-l-btn hp-swaps hp-when-pending hp-l-overturn")}
			</div>`;
        const pens = [button("penalty_minor", "hp-l-btn hp-l-pen", "MIN 2:00", side), button("penalty_major", "hp-l-btn hp-l-pen", "MAJ 5:00", side), button("delayed_penalty", "hp-l-btn hp-l-pen", "DELAYED", side)];
        const top = side === "away" ? [goal, chance, swaps] : [swaps, chance, goal];
        const bottom = side === "away" ? pens : [...pens].reverse();
        return `<section class="hp-half hp-half-${side}" data-side="${side}">
			<div class="hp-l-band"><span class="hp-l-team">${side === "home" ? "HOME" : "AWAY"} · ${escapeHtml(t)}</span><b class="hp-l-prob">—</b></div>
			<div class="hp-l-mkt"><span class="hp-l-touch">Kalshi —/—</span><span class="hp-l-paper">paper —</span></div>
			<div class="hp-l-grid">${top.join("")}${bottom.join("")}</div>
		</section>`;
    };
    return `<div class="hp-landscape">
		<div class="hp-l-head">
			<button type="button" class="hp-exit btn btn-small">Games</button>
			<span class="hp-l-title">${escapeHtml(title)}</span>
			<span class="hp-l-status" role="status"></span>
			<span class="hp-l-quote hp-none">quote state</span>
			<button type="button" class="tap-btn tap-pull hp-kill">KILL</button>
		</div>
		<div class="hp-l-halves">
			${half("away")}
			<div class="hp-l-centre">
				<div class="hp-l-clock"><span class="hp-l-time">—</span><small class="hp-l-sub">—</small></div>
				<div class="hp-l-startstop">
					${button("clock_start", "hp-l-btn hp-l-start")}
					${button("clock_stop", "hp-l-btn hp-l-stop")}
				</div>
				${button("danger", "hp-l-btn hp-l-danger")}
				${button("undo", "hp-l-btn hp-l-undo")}
				<button type="button" class="hp-l-btn hp-l-more hp-more-open">MORE…</button>
			</div>
			${half("home")}
		</div>
		${renderMoreSheet(teams)}
	</div>`;
}
/** The rare kinds, clock corrections and the frame events, opened from MORE and closed by a tap or Close. */
function renderMoreSheet(teams) {
    const a = teams.away, h = teams.home;
    return `<div class="hp-more" hidden>
		<div class="hp-more-head"><span>More</span><button type="button" class="btn btn-small hp-more-close">Close</button></div>
		<div class="hp-more-grid">
			${button("period_start", "hp-l-btn hp-period")}${button("period_end", "hp-l-btn hp-period")}${button("final", "hp-l-btn hp-final")}
			${button("penalty_double_minor", "hp-l-btn hp-l-pen", `2×MIN ${a}`, "away")}${button("penalty_coincidental", "hp-l-btn hp-l-pen")}${button("penalty_double_minor", "hp-l-btn hp-l-pen", `2×MIN ${h}`, "home")}
			${button("penalty_expired", "hp-l-btn", `PEN OVER ${a}`, "away")}${button("delayed_penalty_off", "hp-l-btn")}${button("penalty_expired", "hp-l-btn", `PEN OVER ${h}`, "home")}
			${button("penalty_shot", "hp-l-btn", `PEN SHOT ${a}`, "away")}${button("penalty_shot_miss", "hp-l-btn")}${button("penalty_shot", "hp-l-btn", `PEN SHOT ${h}`, "home")}
			${button("shootout_winner", "hp-l-btn hp-so", `SO WIN ${a}`, "away")}${holdCell("hp-l-btn")}${button("shootout_winner", "hp-l-btn hp-so", `SO WIN ${h}`, "home")}
		</div>
		<div class="hp-more-clock">
			${button("clock_nudge", "hp-l-btn hp-nudge hp-keep-open", "−10", null, -10)}${button("clock_nudge", "hp-l-btn hp-nudge hp-keep-open", "−1", null, -1)}${button("clock_nudge", "hp-l-btn hp-nudge hp-keep-open", "+1", null, 1)}${button("clock_nudge", "hp-l-btn hp-nudge hp-keep-open", "+10", null, 10)}
			<span class="hp-clock-set"><input class="hp-clock-input" inputmode="numeric" placeholder="mm:ss" aria-label="Clock, minutes and seconds remaining" maxlength="5" /><button type="button" class="tap-btn hp-l-btn hp-set hp-keep-open">${escapeHtml(exports.HOCKEY_LABELS.clock_set)}</button></span>
		</div>
	</div>`;
}
function landscapePaint(view, teams, nowServerMs) {
    const st = view?.latest?.state;
    const none = { prob: { home: "—", away: "—" }, touch: { home: "Kalshi —/—", away: "Kalshi —/—" }, paper: { home: "paper —", away: "paper —" }, time: view ? "no state" : "loading…", sub: "", quote: { cls: "hp-none", text: "quote state" }, run: "dead" };
    if (!view || !(0, win_prob_1.isWpHockeyState)(st))
        return none;
    const pHome = view.latest?.result.homeWp ?? null;
    const quote = (outcome) => (view.quotes ?? []).find((q) => q.outcome === outcome);
    const qh = quote("home"), qa = quote("away");
    const homeTouch = view.depth ? { bid: view.depth.bids[0]?.price ?? null, ask: view.depth.asks[0]?.price ?? null } : { bid: qh?.bid ?? null, ask: qh?.ask ?? null };
    // The away market is the home market's mirror; its own quote wins when linked.
    const awayTouch = qa ? { bid: qa.bid, ask: qa.ask } : { bid: homeTouch.ask === null ? null : 1 - homeTouch.ask, ask: homeTouch.bid === null ? null : 1 - homeTouch.bid };
    const c = (v) => (v === null ? "—" : String(Math.round(v * 100)));
    const level = (l, mirror) => (l ? `${Math.round((mirror ? 1 - l.price : l.price) * 100)}×${l.size}${l.placement ? ` <span class="hp-l-pl hp-lp-${l.placement}">${l.placement}</span>` : ""}` : "—");
    const lad = view.ladder;
    const paper = (side) => {
        if (!lad || lad.pulled === "all")
            return "paper: nothing resting";
        const bid = side === "home" ? (lad.pulled === "bids" ? "off" : level(lad.bids[0], false)) : (lad.pulled === "asks" ? "off" : level(lad.asks[0], true));
        const ask = side === "home" ? (lad.pulled === "asks" ? "off" : level(lad.asks[0], false)) : (lad.pulled === "bids" ? "off" : level(lad.bids[0], true));
        return `paper ${bid} · ${ask}`;
    };
    const secs = localSecondsRemaining(st, nowServerMs);
    const flags = [];
    if (st.pendingGoal)
        flags.push(`REVIEW ${st.pendingGoal === "home" ? teams.home : teams.away}`);
    if (st.delayedPenalty)
        flags.push("DELAYED");
    if (st.pendingPenaltyShot)
        flags.push("PEN SHOT");
    if (st.chance && st.chance.untilMs > nowServerMs)
        flags.push(`CHANCE ${Math.max(0, Math.ceil((st.chance.untilMs - nowServerMs) / 1000))}s`);
    if ((0, exports.holdActive)(st, nowServerMs))
        flags.push(`HOLD ${(0, exports.holdLeft)(st, nowServerMs)}`);
    const stopped = (0, exports.stoppedFor)(st, nowServerMs);
    if (stopped !== null)
        flags.unshift(`STOPPED ${mmss(stopped)}`);
    if ((0, exports.suddenDeathAhead)(st) && st.status === "intermission")
        flags.push("OT NEXT"); // the centre column is narrow; the banner says why
    const strength = view.strength ? view.strength.label : "";
    const quoteState = view.killed ? { cls: "hp-pull", text: "PULL · kill switch" } : view.quote ? { cls: `hp-${view.quote.action}`, text: `${quoteActionText(view.quote, teams)}${view.quote.reasons.length ? ` · ${view.quote.reasons.join(", ")}` : ""}` } : none.quote;
    return {
        prob: { home: (0, win_prob_display_1.formatProbability)(pHome), away: (0, win_prob_display_1.formatProbability)(pHome === null ? null : 1 - pHome) },
        touch: { home: `Kalshi ${c(homeTouch.bid)} / ${c(homeTouch.ask)}`, away: `Kalshi ${c(awayTouch.bid)} / ${c(awayTouch.ask)}` },
        paper: { home: paper("home"), away: paper("away") },
        time: `${formatHockeyClock(st, secs)}${st.clock.running ? " ▶" : ""}`,
        sub: [strength, `${st.awayScore} – ${st.homeScore}`, ...flags].filter(Boolean).join(" · "),
        quote: quoteState,
        run: (0, exports.clockRun)(st),
    };
}
/** pull_side names the team whose goal is building: its YES offers, the other side's YES bids and the under come off. */
function quoteActionText(quote, teams) {
    if (quote.action !== "pull_side")
        return quote.action.toUpperCase();
    const team = quote.side === "home" ? teams.home : teams.away;
    const otherTeam = quote.side === "home" ? teams.away : teams.home;
    return `PULL ${team} offers, ${otherTeam} bids, under`;
}
/**
 * The paper ladder as a mirrored depth chart: one row per level, the bid bar
 * growing left from the price pair, the ask bar growing right, placement and
 * edge on the first row, fair and Kalshi's touch in the footer. Always the
 * same number of rows so a pull never moves the tap grid below it.
 */
function renderLadder(ladder, teams) {
    const rows = Math.max(ladder?.basis.clips.length ?? 0, ladder?.bids.length ?? 0, ladder?.asks.length ?? 0, 3);
    const maxSize = Math.max(1, ...(ladder?.basis.clips ?? []), ...(ladder?.bids ?? []).map((l) => l.size), ...(ladder?.asks ?? []).map((l) => l.size));
    const px = (v) => String(Math.round(v * 100));
    const edgeText = (l) => `${l.placement ? `${l.placement} ` : ""}${l.edge >= 0 ? "+" : ""}${l.edge.toFixed(1)}c`;
    const cell = (l, side, first, off) => {
        const meta = first && l && !off ? `<span class="hp-lmeta hp-lmeta-${side}${l.placement ? ` hp-lp-${l.placement}` : ""}">${escapeHtml(edgeText(l))}</span>` : `<span class="hp-lmeta"></span>`;
        const bar = off
            ? `<span class="hp-lbar hp-lbar-${side} hp-lbar-off">${first ? "off" : ""}</span>`
            : l
                ? `<span class="hp-lbar hp-lbar-${side}"><i style="width:${Math.max(4, Math.round((l.size / maxSize) * 100))}%"></i><b>${l.size}</b></span>`
                : `<span class="hp-lbar hp-lbar-${side}"></span>`;
        const price = `<span class="hp-lpx hp-lpx-${side}">${l && !off ? px(l.price) : ""}</span>`;
        return side === "bid" ? meta + bar + price : price + bar + meta;
    };
    const all = !ladder || ladder.pulled === "all";
    const body = Array.from({ length: rows }, (_, i) => {
        const bid = ladder?.bids[i], ask = ladder?.asks[i];
        return `<div class="hp-lrow">${cell(bid, "bid", i === 0, all || ladder?.pulled === "bids")}${cell(ask, "ask", i === 0, all || ladder?.pulled === "asks")}</div>`;
    }).join("");
    const book = ladder?.book && (ladder.book.bid !== null || ladder.book.ask !== null) ? `Kalshi ${ladder.book.bid === null ? "—" : px(ladder.book.bid)}/${ladder.book.ask === null ? "—" : px(ladder.book.ask)}` : "no book";
    const foot = !ladder ? "paper: no ladder" : all ? `paper ${teams.home}: nothing resting` : `paper ${teams.home} · fair ${(ladder.fair * 100).toFixed(1)} · ${book} · need ${ladder.requiredEdge}c`;
    return `<div class="hp-lhead"><span>bids</span><span>${escapeHtml(teams.home)} YES</span><span>asks</span></div>${body}<div class="hp-lfoot">${escapeHtml(foot)}</div>`;
}
const kilo = (n) => (n >= 10000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
/** Kalshi's book in the same grammar as the paper ladder, so the rows compare level for level. */
function renderDepth(depth, teams, rows = 3, nowServerMs) {
    const max = Math.max(1, ...(depth?.bids ?? []).map((l) => l.size), ...(depth?.asks ?? []).map((l) => l.size));
    const bar = (l, side) => l
        ? `<span class="hp-lbar hp-lbar-${side}"><i style="width:${Math.max(3, Math.round((l.size / max) * 100))}%"></i><b>${escapeHtml(kilo(l.size))}</b></span>`
        : `<span class="hp-lbar hp-lbar-${side}"></span>`;
    const px = (l) => (l ? String(Math.round(l.price * 100)) : "");
    const body = Array.from({ length: rows }, (_, i) => {
        const b = depth?.bids[i], a = depth?.asks[i];
        return `<div class="hp-lrow hp-drow"><span class="hp-lmeta"></span>${bar(b, "bid")}<span class="hp-lpx hp-lpx-bid">${px(b)}</span><span class="hp-lpx hp-lpx-ask">${px(a)}</span>${bar(a, "ask")}<span class="hp-lmeta"></span></div>`;
    }).join("");
    const age = depth && nowServerMs !== undefined ? Math.max(0, Math.round((nowServerMs - Date.parse(depth.asOf)) / 1000)) : null;
    const foot = depth ? `Kalshi ${teams.home} YES · top ${Math.max(depth.bids.length, depth.asks.length)} levels${age !== null ? ` · ${age}s ago` : ""}` : `Kalshi ${teams.home} YES · no depth yet`;
    return `<div class="hp-lhead"><span>bids</span><span>Kalshi</span><span>asks</span></div>${body}<div class="hp-lfoot">${escapeHtml(foot)}</div>`;
}
function cents(v) {
    return typeof v === "number" && Number.isFinite(v) ? String(Math.round(v * 100)) : "—";
}
/** The top of the pad: score, clock, strength, the price, Kalshi, the quote state and the reconciliation line. */
function renderHockeyBanner(view, teams, nowServerMs, unsent) {
    const st = view?.latest?.state;
    if (!view || !(0, win_prob_1.isWpHockeyState)(st))
        return `<div class="hp-line hp-big">${escapeHtml(teams.away)} – ${escapeHtml(teams.home)}</div><div class="hp-line">${view ? "no state yet" : "loading…"}</div>`;
    const secs = localSecondsRemaining(st, nowServerMs);
    const flags = [];
    if (st.pendingGoal)
        flags.push(`REVIEW ${st.pendingGoal === "home" ? teams.home : teams.away}`);
    if (st.delayedPenalty)
        flags.push(`DELAYED on ${st.delayedPenalty === "home" ? teams.home : teams.away}`);
    if (st.pendingPenaltyShot)
        flags.push(`PEN SHOT ${st.pendingPenaltyShot === "home" ? teams.home : teams.away}`);
    if (st.goaliePulled.away)
        flags.push(`${teams.away} EN`);
    if (st.goaliePulled.home)
        flags.push(`${teams.home} EN`);
    if (st.dangerUntilMs !== null && st.dangerUntilMs > nowServerMs)
        flags.push("DANGER");
    if (st.chance && st.chance.untilMs > nowServerMs)
        flags.push(`CHANCE ${st.chance.side === "home" ? teams.home : teams.away} ${Math.max(0, Math.ceil((st.chance.untilMs - nowServerMs) / 1000))}s`);
    if ((0, exports.holdActive)(st, nowServerMs))
        flags.push(`HOLD ${(0, exports.holdLeft)(st, nowServerMs)}`);
    const stopped = (0, exports.stoppedFor)(st, nowServerMs);
    if (stopped !== null)
        flags.unshift(`STOPPED ${mmss(stopped)}`);
    if ((0, exports.suddenDeathAhead)(st) && st.status === "intermission")
        flags.push("OT NEXT · no HOLD");
    const strength = view.strength ? `${view.strength.label}${view.strength.nextChangeIn !== null ? ` · ${Math.floor(view.strength.nextChangeIn / 60)}:${String(view.strength.nextChangeIn % 60).padStart(2, "0")}` : ""}` : "";
    const r = view.latest?.result;
    const hockey = view.hockey ?? r?.hockey ?? null;
    const pHome = r?.homeWp ?? null;
    const pAway = pHome === null ? null : 1 - pHome;
    const total = view.latest?.pregame.odds?.total ?? 6.5;
    const pOver = hockey?.totals[String(total)] ?? null;
    const quote = (outcome) => (view.quotes ?? []).find((q) => q.outcome === outcome);
    const qh = quote("home"), qa = quote("away"), qt = quote("total"), qs = quote("spread");
    // The linked total's line is in its ticker (…-7 = over 6.5); the spread's side is its team suffix (…-COL2 = COL by over 1.5).
    const totalLinked = qt ? Number(qt.ticker.split("-").pop()) - 0.5 : null;
    const pOverLinked = totalLinked !== null ? hockey?.totals[String(totalLinked)] ?? null : null;
    const spreadTeam = qs ? qs.ticker.split("-").pop()?.replace(/2$/, "") ?? "" : "";
    const pSpread = qs && hockey ? (spreadTeam === teams.home ? hockey.spreads["1.5"] ?? null : hockey.spreads["-1.5"] === undefined ? null : 1 - hockey.spreads["-1.5"]) : null;
    const extra = [
        qt ? `O${totalLinked} ${(0, win_prob_display_1.formatProbability)(pOverLinked)} · ${cents(qt.bid)}/${cents(qt.ask)}` : "",
        qs ? `${spreadTeam} −1.5 ${(0, win_prob_display_1.formatProbability)(pSpread)} · ${cents(qs.bid)}/${cents(qs.ask)}` : "",
    ].filter(Boolean).join(" · ");
    const review = hockey?.pendingGoal ? ` · if no goal ${(0, win_prob_display_1.formatProbability)(hockey.pendingGoal.overturned)}` : "";
    const quoteState = view.killed ? { cls: "hp-pull", text: "PULL · kill switch" } : view.quote ? { cls: `hp-${view.quote.action}`, text: `${quoteActionText(view.quote, teams)}${view.quote.reasons.length ? ` · ${view.quote.reasons.join(", ")}` : ""}` } : null;
    const ladder = view.killed ? { ...(view.ladder ?? { market: "home", fair: 0, requiredEdge: 0, book: null, bids: [], asks: [], basis: { makerFeeRate: 0, minEdgeCents: 0, clips: [], maxContractsPerSide: 0 } }), pulled: "all" } : view.ladder;
    const feed = view.feed;
    const feedDisagrees = feed && feed.status !== "pre" && (feed.homeScore !== st.homeScore || feed.awayScore !== st.awayScore);
    const feedLine = feed ? `feed ${feed.awayScore}–${feed.homeScore} P${feed.period} ${Math.floor(feed.secondsRemaining / 60)}:${String(Math.floor(feed.secondsRemaining % 60)).padStart(2, "0")}` : "no feed";
    const fit = view.latest?.pregame;
    const meta = [feedLine, view.inputAge === null || view.inputAge === undefined ? "no input yet" : `input ${view.inputAge}s ago`, fit?.rates ? `rates ${fit.rates.home.toFixed(2)}/${fit.rates.away.toFixed(2)} (${fit.source ?? "?"})` : "NO RATES", r?.reason ? `model: ${r.reason}` : ""].filter(Boolean);
    if (unsent)
        meta.push(`${unsent} unsent`);
    return `<div class="hp-line hp-big"><span>${escapeHtml(teams.away)} ${st.awayScore}</span><span class="hp-clock">${escapeHtml(formatHockeyClock(st, secs))}${st.clock.running ? " ▶" : ""}</span><span>${st.homeScore} ${escapeHtml(teams.home)}</span></div>
		<div class="hp-line hp-flags${flags.length ? " hp-flags-on" : ""}">${escapeHtml([strength, ...flags].filter(Boolean).join(" · ") || "even strength")}</div>
		<div class="hp-line hp-big hp-price"><span>${escapeHtml(teams.away)} ${escapeHtml((0, win_prob_display_1.formatProbability)(pAway))}</span><span class="hp-total">O${escapeHtml(String(total))} ${escapeHtml((0, win_prob_display_1.formatProbability)(pOver))}</span><span>${escapeHtml((0, win_prob_display_1.formatProbability)(pHome))} ${escapeHtml(teams.home)}</span></div>
		<div class="hp-line hp-market">Kalshi ${escapeHtml(teams.away)} ${cents(qa?.bid)}/${cents(qa?.ask)} · ${escapeHtml(teams.home)} ${cents(qh?.bid)}/${cents(qh?.ask)}${escapeHtml(review)}</div>
		<div class="hp-line hp-market hp-market-more">${escapeHtml(extra || "no total / puck line linked")}</div>
		<div class="hp-line hp-quote ${quoteState?.cls ?? "hp-none"}">${quoteState ? escapeHtml(quoteState.text) : "quote state: not wired yet"}</div>
		<div class="hp-ladder${ladder?.pulled ? " hp-ladder-pulled" : ""}">${renderLadder(ladder, teams)}</div>
		<div class="hp-ladder hp-depth">${renderDepth(view.depth, teams, Math.max(ladder?.basis.clips.length ?? 0, view.depth?.bids.length ?? 0, view.depth?.asks.length ?? 0, 3), nowServerMs)}</div>
		<div class="hp-line hp-meta${feedDisagrees ? " hp-disagree" : ""}">${escapeHtml(meta.join(" · "))}</div>`;
}
function draftFromOdds(odds, gameType) {
    const s = (v) => (v === undefined ? "" : String(v));
    return {
        homeMl: s(odds?.moneyline?.home), awayMl: s(odds?.moneyline?.away), total: s(odds?.total),
        over: s(odds?.totalOdds?.over), under: s(odds?.totalOdds?.under),
        spread: s(odds?.spreadLine), spreadHome: s(odds?.spreadOdds?.home), spreadAway: s(odds?.spreadOdds?.away), gameType,
    };
}
/** The draft → the route's odds body, or the field that is wrong. */
function oddsFromDraft(d) {
    const num = (v) => (v.trim() === "" ? null : Number(v));
    const home = num(d.homeMl), away = num(d.awayMl);
    if (home === null || away === null || !Number.isFinite(home) || !Number.isFinite(away) || Math.abs(home) < 100 || Math.abs(away) < 100)
        return { error: "moneyline: both American prices, |x| ≥ 100" };
    const odds = { provider: "operator", moneyline: { home, away } };
    const total = num(d.total);
    if (total !== null) {
        const over = num(d.over), under = num(d.under);
        if (!Number.isFinite(total) || !Number.isInteger(total * 2))
            return { error: "total: a line in halves" };
        if (over === null || under === null || !Number.isFinite(over) || !Number.isFinite(under))
            return { error: "total: over and under prices" };
        odds.total = total;
        odds.totalOdds = { over, under };
    }
    const spread = num(d.spread);
    if (spread !== null) {
        if (!Number.isFinite(spread))
            return { error: "puck line: a number, positive = home favoured" };
        odds.spreadLine = spread;
        const sh = num(d.spreadHome), sa = num(d.spreadAway);
        if (sh !== null && sa !== null && Number.isFinite(sh) && Number.isFinite(sa))
            odds.spreadOdds = { home: sh, away: sa };
    }
    return { odds };
}
/** Shin de-vig of a two-way American pair, for the panel's "what the book says" line (server: hockey/calibrate.ts). */
function shinHome(home, away) {
    const imp = (x) => (x > 0 ? 100 / (x + 100) : -x / (100 - x));
    const i1 = imp(home), i2 = imp(away);
    if (i1 + i2 <= 1)
        return i1 / (i1 + i2);
    const fair = (z) => [i1, i2].map((p) => (Math.sqrt(z * z + 4 * (1 - z) * p * p) - z) / (2 * (1 - z)));
    let lo = 0, hi = 0.5;
    for (let k = 0; k < 80; k++) {
        const z = (lo + hi) / 2;
        const [a, b] = fair(z);
        if (a + b > 1)
            lo = z;
        else
            hi = z;
    }
    const [a, b] = fair((lo + hi) / 2);
    return a / (a + b);
}
const american = (n) => (n > 0 ? `+${n}` : String(n));
/** The two anchors side by side: the book's line (and its open) and the exchange's book, with which one the rates use. */
function renderAnchors(odds, teams) {
    if (!odds)
        return "no pre-game line yet";
    const parts = [];
    if (odds.moneyline) {
        const open = odds.openMoneyline ? ` (open ${american(odds.openMoneyline.away)}/${american(odds.openMoneyline.home)})` : "";
        parts.push(`${odds.provider} ${american(odds.moneyline.away)}/${american(odds.moneyline.home)}${open}: ${teams.home} ${(0, win_prob_display_1.formatProbability)(shinHome(odds.moneyline.home, odds.moneyline.away))}`);
    }
    const ex = odds.exchange;
    if (ex)
        parts.push(`${ex.venue} ${teams.home} ${(0, win_prob_display_1.formatProbability)(ex.pHome)} · ${(ex.spread * 100).toFixed(0)}c wide · ${Math.round(ex.volume24h).toLocaleString()} in 24h${ex.qualified ? "" : ` · not used: ${ex.reason ?? "?"}`}`);
    parts.push(`anchor: ${odds.anchor === "exchange" ? ex?.venue ?? "exchange" : odds.provider}`);
    return parts.join(" — ");
}
/** The pre-game screen: the loaded prices, editable, a recalibrate button and the fit beside Kalshi's book. */
function renderPregamePanel(draft, view, fit, message, teams = { home: "home", away: "away" }) {
    const field = (name, label, placeholder) => `<label class="hp-field"><span>${escapeHtml(label)}</span><input class="hp-odds" data-field="${name}" inputmode="decimal" placeholder="${escapeHtml(placeholder)}" value="${escapeHtml(draft[name])}" /></label>`;
    const pre = view?.latest?.pregame;
    const r = view?.latest?.result;
    const qh = view?.quotes.find((q) => q.outcome === "home");
    const mid = qh && typeof qh.bid === "number" && typeof qh.ask === "number" ? (qh.bid + qh.ask) / 2 : null;
    const lines = [];
    if (pre?.rates)
        lines.push(`rates home ${pre.rates.home.toFixed(2)} · away ${pre.rates.away.toFixed(2)} goals (${pre.source ?? "?"}${pre.frozen ? ", frozen" : ""})`);
    if (fit)
        lines.push(`fit ${fit.quality}: P(home) ${(0, win_prob_display_1.formatProbability)(fit.fitted.pHome)} (res ${(fit.residuals.pHome * 100).toFixed(2)} pp)${fit.fitted.pOver !== null ? `, P(over) ${(0, win_prob_display_1.formatProbability)(fit.fitted.pOver)}` : ", no total: split at 6.1"}${fit.fitted.pHomeCover !== null && fit.residuals.pHomeCover !== null ? `, puck line residual ${(fit.residuals.pHomeCover * 100).toFixed(1)} pp` : ""}`);
    if (r)
        lines.push(`model P(home) ${(0, win_prob_display_1.formatProbability)(r.homeWp)} · Kalshi mid ${mid === null ? "—" : (0, win_prob_display_1.formatProbability)(mid)}`);
    if (message)
        lines.push(message);
    return `<div class="hp-pregame-form">
		<div class="hp-fields">
			${field("awayMl", "away ML", "+150")}
			${field("homeMl", "home ML", "-180")}
			${field("total", "total", "6.5")}
			${field("over", "over", "-110")}
			${field("under", "under", "-110")}
			${field("spread", "puck line (+ = home fav)", "1.5")}
			${field("spreadHome", "home PL odds", "+160")}
			${field("spreadAway", "away PL odds", "-195")}
		</div>
		<div class="hp-pregame-actions">
			<select class="hp-game-type form-select" aria-label="Game type">
				<option value="regular"${draft.gameType === "regular" ? " selected" : ""}>regular season</option>
				<option value="playoff"${draft.gameType === "playoff" ? " selected" : ""}>playoff</option>
			</select>
			<button type="button" class="btn btn-small hp-recalibrate">recalibrate</button>
			<button type="button" class="btn btn-small hp-setup-close">close</button>
		</div>
		<div class="hp-anchors">${escapeHtml(renderAnchors(pre?.odds, teams))}</div>
		<div class="hp-anchor-actions">
			<button type="button" class="btn btn-small hp-anchor" data-anchor="exchange"${pre?.odds?.exchange ? "" : " disabled"}>use ${escapeHtml(pre?.odds?.exchange?.venue ?? "exchange")} mid</button>
			<button type="button" class="btn btn-small hp-anchor" data-anchor="book"${pre?.odds?.moneyline ? "" : " disabled"}>use ${escapeHtml(pre?.odds?.provider ?? "book")} line</button>
		</div>
		<div class="hp-fit">${lines.map((l) => `<div>${escapeHtml(l)}</div>`).join("")}</div>
	</div>`;
}
function eventLabel(e, teams) {
    const label = exports.HOCKEY_LABELS[e.kind] ?? e.kind;
    const team = e.side ? ` ${e.side === "home" ? teams.home : teams.away}` : "";
    if (e.kind === "clock_nudge" && e.value !== null)
        return `${e.value > 0 ? "+" : ""}${e.value}s`;
    if (e.kind === "clock_set" && e.value !== null)
        return `SET ${mmss(e.value)}`;
    if (e.kind === "hold" && e.value !== null)
        return `HOLD ${mmss(e.value)}`;
    if (e.kind === "game_type")
        return e.value === 3 ? "playoff" : "regular season";
    return `${label}${team}`;
}
const cents1 = (v) => (v === null || v === undefined ? "—" : String(Math.round(v * 100)));
const signedCents = (v) => `${v >= 0 ? "+" : "−"}${(Math.abs(v) * 100).toFixed(1)}c`;
const seconds = (ms) => `${ms >= 0 ? "+" : "−"}${(Math.abs(ms) / 1000).toFixed(1)} s`;
/** One tap's reaction line: the model before → after, Kalshi before → settled, the lead, the width, a goal's pick-off. */
function renderReaction(r) {
    const settled = r.market["30"]?.mid ?? r.market["20"]?.mid ?? r.market["10"]?.mid ?? null;
    const parts = [
        r.model.before !== null || r.model.after !== null ? `model ${cents1(r.model.before)}→${cents1(r.model.after)}${r.model.agrees === null ? "" : r.model.agrees ? " ✓" : " ✗"}` : "",
        r.base !== null ? `Kalshi ${cents1(r.base)}→${cents1(settled)}${r.eventual !== null ? ` (${signedCents(r.eventual)})` : ""}` : "",
        r.leadMs !== null ? `lead ${seconds(r.leadMs)}` : r.eventual !== null ? "no move" : "",
        r.book.width0 !== null ? `width ${cents1(r.book.width0)}${r.book.widest10 !== null && r.book.widest10 > r.book.width0 ? `→${cents1(r.book.widest10)}` : ""}c${r.book.returnMs !== null ? ` back ${seconds(r.book.returnMs)}` : ""}` : "",
        r.markout ? `pick-off ${r.markout.pickOffCents.toFixed(1)}c${r.markout.pullInTime === null ? "" : r.markout.pullInTime ? ", pull in time" : ", pull late"}` : "",
    ].filter(Boolean);
    return parts.length ? `<span class="hp-ev-rx">${escapeHtml(parts.join(" · "))}</span>` : "";
}
/** The event log, newest first: every tap, struck through when undone, italic while unsent, with its reaction once the series has one. */
function renderHockeyHistory(items, teams, limit = 60, reactions = new Map()) {
    return items
        .slice(-limit)
        .reverse()
        .map((e) => {
        const at = new Date(e.atMs).toLocaleTimeString(undefined, { hour12: false });
        const cls = [e.undone ? "tap-undone" : "", e.pending ? "hp-pending-row" : ""].filter(Boolean).join(" ");
        const rx = e.seq !== null && !e.undone ? reactions.get(e.seq) : undefined;
        return `<li class="${cls}"><span class="hp-ev-time">${escapeHtml(at)}</span> <b>${escapeHtml(eventLabel(e, teams))}</b>${e.feed ? ' <small class="hp-ev-feed">feed</small>' : ""}${e.pending ? " …" : ""}${rx ? renderReaction(rx) : ""}</li>`;
    })
        .join("");
}
/** The game so far in one line: the model against the market, the taps' leads, the mirror. */
function renderSeriesSummary(series) {
    if (!series)
        return "no history yet";
    const parts = [];
    if (series.live)
        parts.push(`model − Kalshi: mean ${(series.live.meanAbs * 100).toFixed(1)}c, within 2c ${Math.round(series.live.within2c * 100)}% of ${series.live.n} samples`);
    if (series.leads.moved)
        parts.push(`${series.leads.moved} tap${series.leads.moved === 1 ? "" : "s"} moved the market, tap first ${series.leads.tapFirst}${series.leads.medianLeadMs !== null ? `, median lead ${seconds(series.leads.medianLeadMs)}` : ""}`);
    else if (series.events.length)
        parts.push(`${series.events.length} tap${series.events.length === 1 ? "" : "s"}, none moved the market yet`);
    if (series.mirror)
        parts.push(`mirror ${(series.mirror.meanAbs * 100).toFixed(1)}c`);
    if (series.outcome)
        parts.push(`Brier model ${series.outcome.model.brier} · market ${series.outcome.market.brier}`);
    return escapeHtml(parts.join(" · ") || `${series.samples} book samples, no live comparison yet`);
}
const CHART = { w: 360, h: 200, padL: 30, padR: 8, padT: 14, padB: 18 };
/** Kinds the chart marks: goals as a full line in the team's colour, period starts dashed, penalties as a tick. */
const MARK_KINDS = { goal: "goal", period_start: "period", penalty_minor: "pen", penalty_double_minor: "pen", penalty_major: "pen" };
/**
 * The price chart: Kalshi's bid–ask band and mid, the model's fair as a step,
 * goals, period starts and penalties marked. An SVG with a fixed viewBox, so
 * it scales to the phone's width. Pure.
 */
function renderHockeyChart(series, teams) {
    const book = series?.book ?? [];
    const model = series?.model ?? [];
    if (!book.length && !model.length)
        return `<div class="hp-chart-empty">${series ? "no price history yet" : "no history yet"}</div>`;
    const { w, h, padL, padR, padT, padB } = CHART;
    const ts = [...book.map((p) => p.t), ...model.map((m) => m.t)];
    const t0 = Math.min(...ts);
    const span = Math.max(Math.max(...ts) - t0, 60000);
    const values = [...book.flatMap((p) => [p.bid, p.ask, p.mid]), ...model.map((m) => m.fair)].filter((v) => v !== null);
    let lo = Math.max(0, Math.floor((Math.min(...values) - 0.03) * 20) / 20);
    let hi = Math.min(1, Math.ceil((Math.max(...values) + 0.03) * 20) / 20);
    if (hi - lo < 0.2) {
        const mid = (hi + lo) / 2;
        lo = Math.max(0, mid - 0.1);
        hi = Math.min(1, mid + 0.1);
    }
    const x = (t) => Math.round((padL + ((t - t0) / span) * (w - padL - padR)) * 10) / 10;
    const y = (v) => Math.round((padT + ((hi - v) / (hi - lo)) * (h - padT - padB)) * 10) / 10;
    const step = hi - lo > 0.3 ? 0.1 : 0.05;
    const grid = [];
    for (let v = Math.ceil(lo / step - 1e-9) * step; v <= hi + 1e-9; v += step) {
        grid.push(`<line class="hp-ch-grid" x1="${padL}" x2="${w - padR}" y1="${y(v)}" y2="${y(v)}"/><text class="hp-ch-label" x="${padL - 3}" y="${y(v) + 3}" text-anchor="end">${Math.round(v * 100)}</text>`);
    }
    const xLabels = [];
    for (let k = 0; k <= 3; k++) {
        const t = t0 + (span * k) / 3;
        xLabels.push(`<text class="hp-ch-label" x="${x(t)}" y="${h - 4}" text-anchor="${k === 0 ? "start" : k === 3 ? "end" : "middle"}">${new Date(t).toLocaleTimeString(undefined, { hour12: false, hour: "2-digit", minute: "2-digit" })}</text>`);
    }
    // The band and the mid, broken where a sample has no touch.
    const runs = [];
    for (const p of book) {
        if (p.bid === null || p.ask === null || p.mid === null) {
            if (runs[runs.length - 1]?.length)
                runs.push([]);
            continue;
        }
        if (!runs.length)
            runs.push([]);
        runs[runs.length - 1].push(p);
    }
    const band = runs.filter((r) => r.length).map((r) => `<polygon class="hp-ch-band" points="${[...r.map((p) => `${x(p.t)},${y(p.ask)}`), ...[...r].reverse().map((p) => `${x(p.t)},${y(p.bid)}`)].join(" ")}"/>`).join("");
    const mid = runs.filter((r) => r.length).map((r) => `<polyline class="hp-ch-mid" points="${r.map((p) => `${x(p.t)},${y(p.mid)}`).join(" ")}"/>`).join("");
    let modelPath = "";
    if (model.length) {
        const d = model.map((m, i) => `${i === 0 ? "M" : "V"}${i === 0 ? `${x(m.t)},` : ""}${y(m.fair)}H${x(i + 1 < model.length ? model[i + 1].t : t0 + span)}`).join("");
        modelPath = `<path class="hp-ch-model" d="${d}"/>`;
    }
    const marks = (series?.events ?? []).flatMap((e) => {
        const kind = MARK_KINDS[e.kind];
        if (!kind)
            return [];
        const xe = x(e.ts);
        // A label near the right edge sits to the left of its line so it stays inside the box.
        const label = (text, cls = "") => (xe > w - padR - 32 ? `<text class="hp-ch-mark-label${cls}" x="${xe - 2}" y="${padT - 3}" text-anchor="end">${text}</text>` : `<text class="hp-ch-mark-label${cls}" x="${xe + 2}" y="${padT - 3}">${text}</text>`);
        if (kind === "goal")
            return [`<line class="hp-ch-goal hp-ch-goal-${e.side ?? "none"}" x1="${xe}" x2="${xe}" y1="${padT}" y2="${h - padB}"/>${label(`G ${escapeHtml(e.side === "home" ? teams.home : e.side === "away" ? teams.away : "")}`)}`];
        if (kind === "period")
            return [`<line class="hp-ch-period" x1="${xe}" x2="${xe}" y1="${padT}" y2="${h - padB}"/>${label(e.period === null ? "P" : `P${e.period}`, " hp-ch-period-label")}`];
        return [`<line class="hp-ch-pen" x1="${xe}" x2="${xe}" y1="${h - padB - 8}" y2="${h - padB}"/>`];
    }).join("");
    return `<svg class="hp-chart-svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="Model and Kalshi over the game">${grid.join("")}${band}${mid}${modelPath}${marks}${xLabels.join("")}</svg>
		<div class="hp-chart-legend"><span class="hp-ch-lg-model">model</span><span class="hp-ch-lg-mid">Kalshi mid</span><span class="hp-ch-lg-band">bid–ask</span><span class="hp-ch-lg-goal">goal</span><span class="hp-ch-lg-pen">penalty</span></div>`;
}
/** mm:ss or plain seconds → seconds remaining, or null. */
function parseClockInput(raw) {
    const t = raw.trim();
    const m = /^(\d{1,2}):(\d{2})$/.exec(t);
    if (m)
        return Number(m[1]) * 60 + Number(m[2]);
    if (/^\d{1,4}$/.test(t))
        return Number(t);
    return null;
}
